/*
 * $Id:: ContactInfoCard.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';

import inputsMaxChars from '../../../../app/inputsMaxChars';
import { isEmail, isNumber } from '../../../../common/utils/service';
import Card from '../../../../components/Card';
import ErrorInput from '../../../../components/ErrorInput';
import { t } from '../../../../providers';
import { requestResult as requestResultSlice, loadingContactInfo } from '../../accountSlice';
import { SLICE_NAME } from '../../constants';

const useStyles = makeStyles((theme) => ({
    fieldLabel: {
        color: theme.palette.text.label,
        fontSize: '0.625rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: 'normal',
    },
    fieldValue: {
        color: theme.palette.text.value,
        fontSize: '0.75rem',
        letterSpacing: 'normal',
        marginBottom: '10px',
    },
    linkValue: {
        color: theme.palette.text.linkBlue,
        fontSize: '0.75rem',
        letterSpacing: 'normal',
        marginBottom: '10px',
        textDecoration: 'underline',
    },
    textInput: {
        height: '2rem',
        width: '9rem',
        borderRadius: '2px',
        marginTop: '5px',
        marginBottom: '10px',
        '@media (max-width:600px)': {
            width: '9.5rem',
        },
    },
    inputError: {
        marginTop: '-5px',
        marginBottom: '5px',
    },
}));

const ContactInfoCard = ({ initialValues, saveDetails, hasAdministratorRole }) => {
    const classes = useStyles();
    const loading = useSelector(loadingContactInfo);

    const response = useSelector(requestResultSlice);
    const dispatch = useDispatch();

    const [info, setInfo] = useState({});
    const [triggerInitialData, setTriggerInitialData] = useState(false); // trigger to get initial data
    const [readonly, setReadonly] = useState(true);

    useEffect(() => {
        const contactInformation = (initialValues || {}).mainAccountDetails || {};
        const newInfo = {
            telephone: {
                value: contactInformation.telephone || '',
                label: 'telephone',
                placeholder: 'type.telephone',
                isDirty: false,
                maxChars: inputsMaxChars.telephone,
            },
            fax: {
                value: contactInformation.fax || '',
                label: 'fax',
                placeholder: 'type.fax',
                isDirty: false,
                maxChars: inputsMaxChars.fax,
            },
            contactEmail: {
                value: contactInformation.contactEmail || '',
                label: 'email',
                placeholder: 'type.email',
                isDirty: false,
                required: true,
                maxChars: inputsMaxChars.email,
            },
            website: {
                value: contactInformation.website || '',
                label: 'website',
                placeholder: 'type.website',
                isDirty: false,
                maxChars: inputsMaxChars.website,
            },
        };

        setReadonly(true);
        setInfo(newInfo);
    }, [initialValues, triggerInitialData]);

    useEffect(() => {
        if (response && response.ok) {
            dispatch({ type: `${SLICE_NAME}/requestResult`, payload: null });
            setReadonly(true);
        }
    }, [response, dispatch]);

    const handleValueChange = (event) => {
        const value = event.target.value;
        const keyName = event.target.name;
        const field = { ...info[keyName], value, isDirty: true };
        if (!field.maxChars || (field.maxChars && value.length <= field.maxChars)) {
            let errorMessage = null;
            if (field.required && !value) errorMessage = 'required.field';
            if (value) {
                if (keyName === 'contactEmail' && !isEmail(value)) errorMessage = 'invalid.email.format';
                if ((keyName === 'fax' || keyName === 'telephone') && !isNumber(value)) errorMessage = 'invalid.format';
            }
            field.errorMessage = errorMessage;
            setInfo({ ...info, [keyName]: field });
        }
    };

    const cancelCard = (event) => {
        event.preventDefault();

        setTriggerInitialData(!triggerInitialData);
        setReadonly(true);
    };

    const saveCard = (event) => {
        event.preventDefault();

        const updatedInfo = Object.keys(info).reduce((acc, cur) => (info[cur].isDirty ? { ...acc, [cur]: info[cur].value } : { ...acc }), {});

        const hasErrors = Object.keys(info).some((field) => !!info[field].errorMessage);

        let infoWithErrorMessages = { ...info };
        const hasRequiredValues = Object.keys(info).some((field) => {
            let result = false;
            if (info[field].required) {
                if (!info[field].value) {
                    infoWithErrorMessages[field].errorMessage = 'required.field';
                }
                result = !!info[field].value;
            }
            return result;
        });
        setInfo(infoWithErrorMessages);

        const canProceed = hasRequiredValues && !hasErrors;

        if (canProceed) {
            const payload = { personDetails: { ...updatedInfo } };
            saveDetails(payload, 'ContactInfo');
        }
    };

    const renderItems = (
        <>
            {Object.keys(info).map((keyName, idx) => {
                const { value, label, placeholder, required = false, errorMessage } = info[keyName];
                const valueClass = !value ? classes.fieldValue : classes.linkValue;
                return (
                    <div key={`${keyName}_${idx}`}>
                        {readonly ? (
                            <React.Fragment>
                                <Typography className={classes.fieldLabel}>
                                    {t(label)} {required && ' *'}
                                </Typography>
                                <Typography className={valueClass}>{value || '-'}</Typography>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <InputLabel error={!!errorMessage} htmlFor={keyName} className={classes.fieldLabel}>
                                    {t(label)} {required && ' *'}
                                </InputLabel>
                                <Input
                                    id={`${keyName}_${idx}`}
                                    placeholder={t(placeholder)}
                                    name={keyName}
                                    value={value}
                                    onChange={handleValueChange}
                                    className={classes.textInput}
                                    type="text"
                                    error={!!errorMessage}
                                    fullWidth
                                    autoFocus={idx === 0}
                                />
                                {!!errorMessage && <ErrorInput errorMessage={t(errorMessage)} classes={`${classes.inputError}`} />}
                            </React.Fragment>
                        )}
                    </div>
                );
            })}
        </>
    );

    return (
        <Card
            title={'contact.information.title'}
            canEdit={hasAdministratorRole}
            readonly={readonly}
            editCard={() => setReadonly(false)}
            saveCard={saveCard}
            cancelCard={cancelCard}
            cardId={'profile_contact_info_card'}>
            {loading ? <CircularProgress /> : renderItems}
        </Card>
    );
};

export default ContactInfoCard;
